import type { CreateDelegationInput, DelegationView } from '@nest-recruit/shared';
import { AlertTriangle, Info, Plus } from 'lucide-react';
import { useId, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Button } from '../../../../components/Button';
import { DatePicker } from '../../../../components/DatePicker';
import { Field } from '../../../../components/Field';
import { Input } from '../../../../components/Input';
import { PersonPicker, type PersonOption } from '../../../../components/PersonPicker';
import { addDhakaDays, dhakaDayEndIso, dhakaDayStartIso, dhakaToday } from '../../../../lib/dhaka-time';
import { formatClockTime, formatDate, formatDateTime } from '../../../../lib/format';
import { useModule } from '../../../../lib/modules';
import { useToasts } from '../../../../stores/toasts';
import type { ApiError } from '../../../../lib/api-client';
import { useDelegationCheck, type Colleague, type CreateDelegationVariables } from '../../api/users-api';
import { coverDatesProblem, QUICK_COVERS, quickCoverDates } from '../../cover-dates';
import { PENDING_COVER_PREFIX } from './CoverList';
import { coverNoticeWording } from './cover-notice';
import styles from './ArrangeCoverForm.module.css';

interface ArrangeCoverFormProps {
  readonly viewer: { readonly id: string; readonly fullName: string };
  /** Admin may arrange cover for somebody else ("Who is away"); everyone else arranges their own. */
  readonly isAdmin: boolean;
  readonly colleagues: readonly Colleague[];
  /** The page's clock (`useNow`), for when the stand-in will be told (review R28). */
  readonly now: number;
  /**
   * Saves the cover. The page holds the mutation, so its answer still lands after the phone's sheet has closed and this form with it.
   */
  readonly onStart: (
    variables: CreateDelegationVariables,
    handlers: { readonly onSuccess: () => void; readonly onError: (error: ApiError) => void },
  ) => void;
  /** After Start, on the phone, where the form is a sheet that closes. */
  readonly onStarted?: () => void;
}

interface FormValues {
  readonly fromUserId: string;
  readonly toUserId: string | null;
  readonly from: string;
  readonly until: string;
  readonly reason: string;
}

/**
 * "Arrange cover", always open beside the timeline (Option A). It says in one plain sentence exactly what will happen, and asks the
 * server's dry run whether the cover can be saved (the stand-in away too, a cover already in place) before the button is pressed.
 *
 * Nothing is emailed when it is saved: the stand-in hears from N-69's sweep, and the sentence says when that really is (review R28,
 * `cover-notice.ts`). The mockup's "Both of you get an email now" was not picked (DECISIONS-LOG 2026-11-02 Q7), so it never promises one.
 */
export function ArrangeCoverForm({ viewer, isAdmin, colleagues, now, onStart, onStarted }: ArrangeCoverFormProps): ReactNode {
  const fresh = (fromUserId: string): FormValues => {
    // The organisation's day is the Dhaka day (D5): the chosen dates are Dhaka dates, not UTC ones (D36).
    const today = dhakaToday();
    return { fromUserId, toUserId: null, from: today, until: addDhakaDays(today, 7), reason: '' };
  };
  const [values, setValues] = useState<FormValues>(() => fresh(viewer.id));
  const set = (patch: Partial<FormValues>): void => setValues((current) => ({ ...current, ...patch }));

  const whenId = useId();
  const isRequestsOn = useModule('requisitions');
  const showToast = useToasts((state) => state.show);

  const isSelf = values.fromUserId === viewer.id;
  const nameOf = (userId: string | null): string =>
    userId === viewer.id ? viewer.fullName : (colleagues.find((row) => row.id === userId)?.fullName ?? 'Somebody');
  const awayName = nameOf(values.fromUserId);
  const standInName = values.toUserId === null ? null : nameOf(values.toUserId);

  const people: readonly PersonOption[] = colleagues.some((row) => row.id === viewer.id)
    ? colleagues
    : [{ id: viewer.id, fullName: viewer.fullName }, ...colleagues];
  const awayOptions = people.map((row) => (row.id === viewer.id ? { ...row, fullName: `${row.fullName} (you)` } : row));
  const standInOptions = people.filter((row) => row.id !== values.fromUserId);

  // D38: a cover runs to the end of the Until day, so one day (From = Until) is a real cover; the sentence says what cannot be used.
  const datesProblem = coverDatesProblem(values.from, values.until);
  const startsAt = dhakaDayStartIso(values.from);
  const endsAt = dhakaDayEndIso(values.until);
  const input: CreateDelegationInput | null =
    values.toUserId !== null && datesProblem === null && startsAt !== null && endsAt !== null
      ? {
          ...(isSelf ? {} : { fromUserId: values.fromUserId }),
          toUserId: values.toUserId,
          startsAt,
          endsAt,
          reason: values.reason.trim() === '' ? null : values.reason.trim(),
        }
      : null;
  const check = useDelegationCheck(input);

  const pickAway = (fromUserId: string | null): void => {
    if (fromUserId === null) return;
    set({ fromUserId, toUserId: values.toUserId === fromUserId ? null : values.toUserId });
  };

  const submit = (): void => {
    if (input === null || check.problem !== null) return;
    const before = values;
    const preview: DelegationView = {
      id: `${PENDING_COVER_PREFIX}${crypto.randomUUID()}`,
      fromUserId: values.fromUserId,
      fromUserName: awayName,
      toUserId: input.toUserId,
      toUserName: standInName ?? 'Somebody',
      startsAt: input.startsAt,
      endsAt: input.endsAt,
      reason: input.reason,
      revokedAt: null,
      version: 1,
      sentOn: 0,
      waitingTooLong: 0,
    };
    // Act first (ADR 0002): the cover is on the timeline and in the list now; the form is ready for the next one.
    setValues(fresh(viewer.id));
    onStarted?.();
    onStart(
      { input, preview },
      {
        onSuccess: () =>
          showToast({ tone: 'success', message: `${preview.toUserName} will cover ${isSelf ? 'your' : `${awayName}'s`} approvals` }),
        // The server's words (a duplicate, a chain, a colleague who is away) say what to change, so they are shown in full, and the
        // form gets back what was typed.
        onError: (error) => {
          setValues(before);
          showToast({ tone: 'danger', message: "That cover wasn't saved", detail: error.message });
        },
      },
    );
  };

  // 06-qol §2: every form submits on Ctrl/Cmd+Enter.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault();
      submit();
    }
  };

  const today = dhakaToday();
  const pickedQuick = QUICK_COVERS.find((choice) => {
    const dates = quickCoverDates(choice.value, today);
    return dates.from === values.from && dates.until === values.until;
  })?.value;

  return (
    <div className={styles.form} onKeyDown={onKeyDown}>
      <div className={styles.intro}>
        <h2 className={styles.title}>Arrange cover</h2>
        <p className={styles.sub}>
          {isSelf
            ? 'While you are away, approvals meant for you go to them. Your own access does not move.'
            : `While ${awayName} is away, approvals meant for them go to the person covering. Their own access does not move.`}
        </p>
      </div>

      {isAdmin && (
        <Field label="Who is away" isRequired>
          {({ inputId }) => (
            <PersonPicker id={inputId} label="Who is away" people={awayOptions} value={values.fromUserId} onChange={pickAway} />
          )}
        </Field>
      )}

      <Field label={isSelf ? 'Who covers for you' : `Who covers for ${awayName}`} isRequired>
        {({ inputId }) => (
          <PersonPicker
            id={inputId}
            label={isSelf ? 'Who covers for you' : `Who covers for ${awayName}`}
            placeholder="Choose a colleague"
            people={standInOptions}
            value={values.toUserId}
            onChange={(toUserId) => set({ toUserId })}
          />
        )}
      </Field>

      <div className={styles.when}>
        <span className={styles.whenLabel} id={whenId}>
          When
        </span>
        <div className={styles.quick} role="group" aria-labelledby={whenId}>
          {QUICK_COVERS.map((choice) => (
            <Button
              key={choice.value}
              variant={pickedQuick === choice.value ? 'solid' : 'outline'}
              size="sm"
              aria-pressed={pickedQuick === choice.value}
              onClick={() => set(quickCoverDates(choice.value, today))}
            >
              {choice.label}
            </Button>
          ))}
        </div>
        <div className={styles.dates}>
          <Field label="From" isRequired>
            {({ inputId }) => <DatePicker id={inputId} label="From" value={values.from} onChange={(from) => set({ from })} />}
          </Field>
          <Field label="Until" isRequired error={datesProblem ?? undefined} hint="The cover runs to the end of this day.">
            {({ inputId }) => <DatePicker id={inputId} label="Until" value={values.until} onChange={(until) => set({ until })} />}
          </Field>
        </div>
      </div>

      <Field label="Why" isOptional hint="Shown in the access log alongside both names.">
        {({ inputId }) => (
          <Input id={inputId} value={values.reason} placeholder="Annual leave" onChange={(event) => set({ reason: event.target.value })} />
        )}
      </Field>

      {input !== null && standInName !== null && (
        <p className={styles.plan} data-testid="cover-plan">
          <Info size={14} aria-hidden />
          <span>
            From <b>{`${formatDate(input.startsAt)} ${formatClockTime(input.startsAt)}`}</b> to <b>{formatDateTime(input.endsAt)}</b>,{' '}
            {standInName} can decide the approvals waiting on {isSelf ? 'you' : awayName}
            {/* Role steps are hire-request steps: said only while Hire requests is on (review R18). */}
            {isRequestsOn && `, including any HR check or Leadership steps ${isSelf ? 'you hold' : 'they hold'}`}, but not requests either of{' '}
            {isSelf ? 'you' : 'them'} raised. Nothing is sent when you
            save: {coverNoticeWording(standInName, input.startsAt, input.endsAt, new Date(now))}
          </span>
        </p>
      )}

      {check.problem !== null && (
        <p className={styles.warn} role="status" data-testid="cover-problem">
          <AlertTriangle size={14} aria-hidden />
          <span>{check.problem}</span>
        </p>
      )}

      <Button variant="solid" icon={Plus} block disabled={input === null || check.problem !== null} onClick={submit}>
        Start the cover
      </Button>

      <p className={styles.note}>
        Only approvals move. They do not gain anything else {isSelf ? 'you' : awayName} can see or do.
      </p>
    </div>
  );
}
